import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {RPaletteColor} from '../../../../models/api/paletteModelRuntime';
import {DraggableSwitcherItem, DraggableSwitcherItemPublicProps} from './draggableSwitcherItem';

/*
 * Props.
 */

interface InternalSwitcherItemProps extends DraggableSwitcherItemPublicProps {
  iconName: IconName;
  hoverIconName: IconName;
  selectedIconName?: IconName;
  color: PaletteColorsEnum | string;
  isInMultiViewButton?: boolean;
}

/*
 * Style.
 */

interface IconStyleProps {
  $isDragging: boolean;
  $iconColor: string;
}
const StyledIconDiv = styled.div<IconStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};

  ${(p) => css`
    color: ${p.$iconColor};
  `};
  ${(p) => addIconDraggingStyle(p.$isDragging)};
`;

function addIconDraggingStyle(isDragging: boolean) {
  if (isDragging) {
    return css`
      display: none;
    `;
  }

  return css`
    *:hover > && {
      display: none;
    }
  `;
}

interface HoverIconStyleProps {
  $isDragging: boolean;
  $color: string;
}
const StyledHoverIconDiv = styled.div<HoverIconStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};

  color: ${(p) => p.$color};
  ${(p) => addHoverIconDraggingStyle(p.$isDragging)}
`;

function addHoverIconDraggingStyle(isDragging: boolean) {
  if (isDragging) {
    return '';
  }

  return css`
    display: none;
    *:hover > && {
      display: flex;
    }
  `;
}

interface SelectedIconStyleProps {
  $iconColor: string;
}
const StyledSelectedIconDiv = styled.div<SelectedIconStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};

  ${(p) => css`
    color: ${p.$iconColor};
    background-color: ${p.theme.foreground};
  `};
`;

/*
 * Component.
 */

export const InternalSwitcherItem: FC<InternalSwitcherItemProps> = (props) => {
  const theme = useUserTheme();
  return (
    <DraggableSwitcherItem {...props}>
      {(isDragging) => renderContent(props, isDragging, theme)}
    </DraggableSwitcherItem>
  );
};

function renderContent(props: InternalSwitcherItemProps, isDragging: boolean, theme: UserTheme) {
  const {isSelected, color, iconName, hoverIconName, selectedIconName, isInMultiViewButton} = props;

  // If our color is a PaletteColorsEnum, use its shade40. Otherwise simply use the specified color.
  const iconColor = RPaletteColor.is(color) ? theme.palette[color].shade40 : color;

  // If the item is selected, render a single icon in selected state.
  if (isSelected && !isDragging) {
    return (
      <StyledSelectedIconDiv $iconColor={iconColor}>
        <Icon name={selectedIconName || iconName} />
      </StyledSelectedIconDiv>
    );
  }

  // Otherwise, render both icons to switch on hover.
  return (
    <>
      <StyledIconDiv $iconColor={iconColor} $isDragging={isDragging}>
        <Icon name={iconName} />
      </StyledIconDiv>
      {/* If we are in the multi-view button we do not need to show a hover state */}
      <StyledHoverIconDiv
        $isDragging={isDragging}
        $color={isInMultiViewButton ? theme.greys.shade60 : iconColor}
      >
        <Icon name={hoverIconName} />
      </StyledHoverIconDiv>
    </>
  );
}
